import { useCallback, useMemo, useState } from 'react';

/**
 * useRowSelection — multi-select state for table rows.
 *
 *   const { selected, isSelected, toggle, toggleAll, allSelected,
 *           someSelected, clear, count } = useRowSelection();
 *
 *   <input type="checkbox" checked={isSelected(id)} onChange={() => toggle(id)} />
 *   <input type="checkbox" checked={allSelected(idsOnPage)}
 *          onChange={() => toggleAll(idsOnPage)} />
 *
 * `selected` is a Set<string>. Pass the set's contents to bulk-action handlers.
 */
export function useRowSelection() {
  const [selected, setSelected] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids) => {
    setSelected((prev) => {
      const all = ids.every((id) => prev.has(id)) && ids.length > 0;
      const next = new Set(prev);
      if (all) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const isSelected = useCallback((id) => selected.has(id), [selected]);

  const allSelected = useCallback((ids) => ids.length > 0 && ids.every((id) => selected.has(id)), [selected]);

  const someSelected = useCallback((ids) => ids.some((id) => selected.has(id)) && !ids.every((id) => selected.has(id)), [selected]);

  return useMemo(() => ({
    selected, count: selected.size,
    isSelected, toggle, toggleAll, clear, allSelected, someSelected,
  }), [selected, isSelected, toggle, toggleAll, clear, allSelected, someSelected]);
}
